import {
  ArrowRightIcon,
  Button,
  EditIcon,
  InputSearch,
  Pagination,
  PlusIcon,
  Select,
  Skeleton,
  Text,
} from '@mercai/clever'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Header, TableSupplier } from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { trackEvent, useParamsState } from '@/helpers'
import { useSupplierList } from '@/services'

import { SupplierDrawerForm, SupplierTabs } from '../../../components'

import { ROUTES_ADMIN } from '@/router'
import styles from './list.module.css'

export const SupplySupplierList = () => {
  const { t } = useTranslation('screenSuppliers')

  const [totalPages, setTotalPages] = useState(1)

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    status: '',
    createdAtBefore: '',
    createdAtAfter: '',
  })

  const [queryParams, setQueryParams] = useParamsState({
    uuid: '',
    show: '',
    form: '',
  })

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({ ...filter, [changedKey]: newValue })
  }

  const { data, isLoading } = useSupplierList({
    params: filter,
  })

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(Math.ceil(data?.data?.count / +filter.limit))
    }
  }, [data])

  useEffect(() => {
    trackEvent('adminTransportSupplierListPageView')
  }, [])

  const onHandleNewRegister = () => {
    setQueryParams({ ...queryParams, show: '', form: 'true', uuid: '' })
  }

  const onHandleEditRegister = (uuid: string) => {
    setQueryParams({ ...queryParams, form: 'true', show: '', uuid })
  }

  return (
    <div>
      <Header title={t('title')} description={t('description')} />

      <SupplierTabs />

      <div className={styles.content}>
        <div>
          <div className={styles.filter}>
            <div className={styles['filter-group']}>
              <InputSearch
                value={filter.search as string}
                onChange={(value) => handleFilter('search', value)}
                className={styles['search-input']}
                translations={{
                  inputPlaceholder: t('searchFilterPlaceholder'),
                }}
              />
            </div>

            <div className={styles['filter-group']}>
              <Button onClick={onHandleNewRegister}>
                <PlusIcon />
                {t('supplierTableNewRegister')}
              </Button>
            </div>
          </div>

          <TableSupplier
            data={data?.data?.results || []}
            isLoading={isLoading}
            actions={{
              title: t('supplierTableActions'),
              key: 'uuid',
              dataKey: 'uuid',
              width: '8rem',
              render: ({ record }) => (
                <div className={styles['action-buttons']}>
                  <Button
                    variant="secondary"
                    onClick={() => onHandleEditRegister(record.uuid || '')}
                    model="square"
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="primary"
                    href={ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.fullPath(
                      record?.uuid || '',
                    )}
                    model="square"
                  >
                    <ArrowRightIcon />
                  </Button>
                </div>
              ),
              renderLoading: (
                <div className={styles['action-buttons']}>
                  <Skeleton height="2.8rem" width="2.8rem" />
                  <Skeleton height="2.8rem" width="2.8rem" />
                </div>
              ),
            }}
          />

          <div className={styles.pagination}>
            <div className={styles['pagination-show-registers']}>
              <Text>
                {t('supplierTableShowRegisters', {
                  showNumber:
                    (data?.data?.count || 0) < +filter.limit
                      ? data?.data?.count
                      : +filter.limit,
                  totalNumber: data?.data?.count || 0,
                })}
              </Text>

              <Select
                value={filter.limit}
                onChange={(value) => handleFilter('limit', value)}
                options={[
                  { label: '10', value: '10' },
                  { label: '20', value: '20' },
                  { label: '50', value: '50' },
                  { label: '100', value: '100' },
                ]}
              />
            </div>

            <Pagination
              totalPages={totalPages}
              onChange={(page) => handleFilter('page', `${page}`)}
              currentPage={+filter.page as number}
            />
          </div>
        </div>
      </div>

      <SupplierDrawerForm
        isOpen={!!queryParams.form}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, form: '', uuid: '' })
        }
        supplierUuid={queryParams.uuid}
      />
    </div>
  )
}
